import { useEffect, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Btn, Field, Area, Select } from "@/components/admin/ui";
import MediaPicker from "@/components/admin/MediaPicker";

export default function Slides() {
  const [slides, setSlides] = useState([]);
  const [picker, setPicker] = useState(null);
  const [settings, setSettings] = useState(null);
  const load = () => api.get("/admin/slides").then((r) => setSlides(r.data)).catch(() => {});
  const loadSettings = () => api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => { load(); loadSettings(); }, []);

  const add = async () => { await api.post("/admin/slides", { headline: "New Slide", message: "", overlay: 50, order: slides.length }); toast.success("Slide added"); load(); };
  const save = async (s) => { await api.put(`/admin/slides/${s.id}`, s); toast.success("Saved"); load(); };
  const del = async (id) => { if (!window.confirm("Delete slide?")) return; await api.delete(`/admin/slides/${id}`); toast.success("Deleted"); load(); };
  const move = async (i, d) => {
    const j = i + d; if (j < 0 || j >= slides.length) return;
    const a = slides[i], b = slides[j];
    await api.put(`/admin/slides/${a.id}`, { order: b.order }); await api.put(`/admin/slides/${b.id}`, { order: a.order }); load();
  };
  const upd = (id, patch) => setSlides(slides.map((s) => s.id === id ? { ...s, ...patch } : s));

  const CTA_DEF = { visible: true, heading: "Ready to start a new healthy you?", subtitle: "Order products by phone or book your consultation today.",
    primary: { visible: true, label: "Call to Order +1 (289) 442-1265", url: "tel:+12894421265" }, secondary: { visible: true, label: "Book an Appointment", url: "/contact" } };
  const cta = { ...CTA_DEF, ...(settings?.home_cta || {}), primary: { ...CTA_DEF.primary, ...(settings?.home_cta?.primary || {}) }, secondary: { ...CTA_DEF.secondary, ...(settings?.home_cta?.secondary || {}) } };
  const setCta = (patch) => setSettings({ ...settings, home_cta: { ...cta, ...patch } });
  const setBtn = (which, patch) => setCta({ [which]: { ...cta[which], ...patch } });
  const saveCta = async () => { try { const { data } = await api.put("/admin/settings", { ...settings, home_cta: cta }); setSettings(data); toast.success("Closing CTA saved"); } catch (e) { toast.error("Could not save"); } };

  return (
    <div data-testid="admin-slides">
      <PageHeader title="Homepage Hero Manager" subtitle="Background slides, headlines, messages and calls-to-action">
        <Btn onClick={add} data-testid="add-slide-btn"><Plus size={16} /> Add Slide</Btn>
      </PageHeader>
      <div className="space-y-5">
        {slides.map((s, i) => (
          <div key={s.id} className="bg-white rounded-2xl border border-stone-200/80 p-6" data-testid={`slide-${i}`}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-emerald-900">Slide {i + 1}</span>
              <div className="flex gap-1">
                <button onClick={() => upd(s.id, { visible: !s.visible })} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500" title="Toggle visibility">{s.visible ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                <button onClick={() => move(i, -1)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><ChevronUp size={16} /></button>
                <button onClick={() => move(i, 1)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><ChevronDown size={16} /></button>
                <button onClick={() => del(s.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-emerald-900">Background image</span>
                  <div className="flex gap-2 mt-1">
                    <input value={s.image_url || ""} onChange={(e) => upd(s.id, { image_url: e.target.value })} placeholder="Image URL" className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-600" />
                    <button onClick={() => setPicker(() => (url) => upd(s.id, { image_url: url }))} className="rounded-lg border border-stone-300 px-3 py-2 text-sm hover:bg-stone-100 flex items-center gap-1"><ImageIcon size={15} /></button>
                  </div>
                  {s.image_url && <img src={s.image_url} alt="" className="mt-2 h-28 w-full rounded-lg object-cover border" />}
                </div>
                <Field label="Mobile image (optional)" value={s.image_mobile_url} onChange={(v) => upd(s.id, { image_mobile_url: v })} />
                <Select label="Focal point" value={s.focal_point || "center"} onChange={(v) => upd(s.id, { focal_point: v })} options={["center", "top", "bottom", "left", "right"].map((x) => ({ value: x, label: x }))} />
              </div>
              <div className="space-y-3">
                <Field label="Headline" value={s.headline} onChange={(v) => upd(s.id, { headline: v })} />
                <Area label="Supporting message" value={s.message} onChange={(v) => upd(s.id, { message: v })} rows={2} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="CTA label" value={s.cta_label} onChange={(v) => upd(s.id, { cta_label: v })} />
                  <Field label="CTA link" value={s.cta_url} onChange={(v) => upd(s.id, { cta_url: v })} />
                </div>
                <label className="block"><span className="text-sm font-medium text-emerald-900">Overlay strength: {s.overlay}%</span>
                  <input type="range" min="0" max="90" value={s.overlay ?? 50} onChange={(e) => upd(s.id, { overlay: Number(e.target.value) })} className="w-full accent-emerald-700 mt-2" /></label>
              </div>
            </div>
            <div className="mt-4 flex justify-end"><Btn onClick={() => save(s)}>Save Slide</Btn></div>
          </div>
        ))}
      </div>

      {settings && (
        <div className="mt-8 bg-white rounded-2xl border border-stone-200/80 p-6" data-testid="home-cta-manager">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-serif text-lg text-emerald-900">Closing Call-to-Action</h3>
              <p className="text-sm text-stone-500">The dark green panel at the bottom of the homepage.</p>
            </div>
            <button onClick={() => setCta({ visible: !(cta.visible !== false) })} data-testid="cta-section-visible" className="flex items-center gap-2 text-sm text-stone-600 hover:text-emerald-800">
              {cta.visible !== false ? <Eye size={16} /> : <EyeOff size={16} />} {cta.visible !== false ? "Section visible" : "Section hidden"}
            </button>
          </div>
          <div className="space-y-4">
            <Field label="Heading" value={cta.heading} onChange={(v) => setCta({ heading: v })} />
            <Area label="Subtitle" value={cta.subtitle} onChange={(v) => setCta({ subtitle: v })} rows={2} />
            <div className="grid md:grid-cols-2 gap-4">
              {[["primary", "Primary button"], ["secondary", "Secondary button"]].map(([key, title]) => (
                <div key={key} className="rounded-xl border border-stone-200 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-emerald-900">{title}</span>
                    <button onClick={() => setBtn(key, { visible: !(cta[key].visible !== false) })} data-testid={`cta-${key}-visible`} className="text-stone-500 hover:text-emerald-800">{cta[key].visible !== false ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                  </div>
                  <Field label="Label" value={cta[key].label} onChange={(v) => setBtn(key, { label: v })} />
                  <Field label="Destination (tel:, /path or https://)" value={cta[key].url} onChange={(v) => setBtn(key, { url: v })} />
                </div>
              ))}
            </div>
            <div className="flex justify-end"><Btn onClick={saveCta} data-testid="save-home-cta-btn">Save Closing CTA</Btn></div>
          </div>
        </div>
      )}

      <MediaPicker open={!!picker} onClose={() => setPicker(null)} onSelect={(url) => picker && picker(url)} />
    </div>
  );
}
